import React, { useEffect, useRef, useState } from "react";
import classes from "./Dropdown.module.scss";
import Chevron from "../Icons/Chevron";
import CrossIcon from "../Icons/CrossIcon";
import TickMark from "../Icons/TickMark";
import SearchIcon from "../Icons/SearchIcon";

export interface DropdownOption {
  label: string;
  value: string;
}

interface DropdownProps {
  options: DropdownOption[];
  value?: DropdownOption | null;
  onChange?: (option: DropdownOption) => void;
  onClear?: () => void;
  showClear?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  multiple?: boolean;
  selectedValues?: DropdownOption[];
  onSelectionChange?: (options: DropdownOption[]) => void;
  searchable?: boolean;
  searchPlaceholder?: string;
}

const Dropdown = ({
  options,
  value = null,
  onChange,
  onClear,
  showClear = false,
  placeholder = "Select",
  disabled = false,
  className = "",
  multiple = false,
  selectedValues = [],
  onSelectionChange,
  searchable = false,
  searchPlaceholder = "Search",
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && searchable) {
      searchInputRef.current?.focus();
    } else {
      setSearchTerm("");
    }
  }, [isOpen, searchable]);

  const toggleOpen = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const handleSelect = (option: DropdownOption) => {
    onChange?.(option);
    setIsOpen(false);
  };

  const isOptionSelected = (option: DropdownOption) => selectedValues.some((selected) => selected.value === option.value);

  const handleToggle = (option: DropdownOption) => {
    const next = isOptionSelected(option) ? selectedValues.filter((selected) => selected.value !== option.value) : [...selectedValues, option];
    onSelectionChange?.(next);
  };

  const handleRemoveChip = (event: React.MouseEvent, option: DropdownOption) => {
    event.stopPropagation();
    onSelectionChange?.(selectedValues.filter((selected) => selected.value !== option.value));
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onClear?.();
    setIsOpen(false);
  };

  const canClear = showClear && !!value && !disabled;

  const visibleOptions = options.filter((option) => option.value !== "" && option.value !== undefined && option.value !== null);

  const filteredOptions =
    searchable && searchTerm ? visibleOptions.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())) : visibleOptions;

  return (
    <div ref={wrapperRef} className={`${classes["dropdown"]} ${className}`}>
      <button
        type="button"
        className={classes["dropdown-trigger"]}
        onClick={toggleOpen}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {multiple ? (
          <span className={selectedValues.length ? classes["dropdown-chips"] : classes["dropdown-placeholder"]}>
            {selectedValues.length ? (
              selectedValues.map((option) => (
                <span key={option.value} className={classes["dropdown-chip"]}>
                  {option.label}
                  <span className={classes["dropdown-chip-remove"]} role="button" aria-label={`Remove ${option.label}`} onClick={(event) => handleRemoveChip(event, option)}>
                    <CrossIcon width={10} height={10} color="#64647A" />
                  </span>
                </span>
              ))
            ) : (
              placeholder
            )}
          </span>
        ) : (
          <span className={value ? classes["dropdown-value"] : classes["dropdown-placeholder"]}>{value?.label ?? placeholder}</span>
        )}
        {!multiple && canClear ? (
          <span className={classes["dropdown-clear"]} role="button" aria-label="Clear selection" onClick={handleClear}>
            <CrossIcon width={12} height={12} color="#64647A" />
          </span>
        ) : (
          <span className={`${classes["dropdown-chevron"]} ${isOpen ? classes["dropdown-chevron--open"] : ""}`}>
            <Chevron width={12} height={12} color="#64647A" />
          </span>
        )}
      </button>

      {isOpen && (
        <div className={classes["dropdown-menu"]}>
          {searchable && (
            <div className={classes["dropdown-search-wrapper"]}>
              <span className={classes["dropdown-search-icon"]}>
                <SearchIcon width={16} height={16} color="#64647A" />
              </span>
              <input
                ref={searchInputRef}
                type="text"
                className={classes["dropdown-search"]}
                placeholder={searchPlaceholder}
                value={searchTerm}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
          )}
          <ul className={classes["dropdown-options"]} role="listbox" aria-multiselectable={multiple}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const selected = multiple ? isOptionSelected(option) : value?.value === option.value;

                return (
                  <li
                    key={`${option.value}-${index}`}
                    role="option"
                    aria-selected={selected}
                    className={`${classes["dropdown-option"]} ${selected && !multiple ? classes["dropdown-option--selected"] : ""} ${
                      multiple ? classes["dropdown-option--checkable"] : ""
                    }`}
                    onClick={() => (multiple ? handleToggle(option) : handleSelect(option))}
                  >
                    {multiple && (
                      <span className={`${classes["dropdown-checkbox"]} ${selected ? classes["dropdown-checkbox--checked"] : ""}`}>
                        {selected && (
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 3L4.5 8.5L2 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        )}
                      </span>
                    )}
                    {option.label} {!multiple && selected && <TickMark color="#226CF5"/>}
                  </li>
                );
              })
            ) : (
              <li className={classes["dropdown-empty"]}>No Items</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Dropdown;
